import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/select/select.js';
import '@shoelace-style/shoelace/dist/components/option/option.js';
import { WithUserProfileConsumer } from '../mixins/with-user-profile-consumer';
import { WithActiveShopConsumer } from '../mixins/with-active-shop-consumer';
import { ApiFactory } from '../api/easypos-api';
import { DeliveryStatus, IDelivery, ISupplier } from '../api/model/requests';
import { resolveRouterPath, router } from '../router';
import { styles as sharedStyles } from '../styles/shared-styles';
import '../components/data-table';
import {
  createArrayDataProvider,
  type DataTableColumnDef,
} from '../components/data-table';

const Base = WithActiveShopConsumer(WithUserProfileConsumer(LitElement));

const STATUS_VARIANT: Record<
  DeliveryStatus,
  'neutral' | 'primary' | 'warning' | 'success' | 'danger'
> = {
  pending: 'warning',
  confirmed: 'success',
  cancelled: 'neutral',
};

const STATUS_LABEL: Record<DeliveryStatus, string> = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  cancelled: 'Cancelled',
};

// Sub-phase U.2a — data-table list of deliveries. Row click → edit page;
// per-row action column carries Confirm + Cancel for pending deliveries
// (owner only). Status filter remains in the top bar.
@customElement('deliveries-list-page')
export class DeliveriesListPage extends Base {
  @state() private _deliveries: IDelivery[] = [];
  @state() private _suppliersById = new Map<string, ISupplier>();
  @state() private _loading = true;
  @state() private _error: string | null = null;
  @state() private _statusFilter = '';
  @state() private _busy = false;

  private _initStarted = false;

  static styles = [
    sharedStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
        padding: 16px;
        max-width: 1200px;
        margin: 0 auto;
        box-sizing: border-box;
        width: 100%;
        gap: 12px;
      }
      header.bar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        flex-wrap: wrap;
        flex: none;
      }
      .filter-bar {
        display: flex;
        gap: 8px;
        align-items: center;
        flex: none;
      }
      app-data-table {
        flex: 1 1 auto;
        min-height: 0;
      }
      sl-alert {
        flex: none;
      }
      .filter-bar sl-select {
        min-width: 180px;
      }
      .row-actions {
        display: flex;
        gap: 2px;
        justify-content: flex-end;
      }
      .empty {
        text-align: center;
        padding: 32px 16px;
        color: var(--sl-color-neutral-500);
      }
      .money {
        font-variant-numeric: tabular-nums;
      }
    `,
  ];

  firstUpdated() {
    if (this.activeShop) void this._init();
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('activeShop') && this.activeShop && !this._initStarted) {
      void this._init();
    }
  }

  private async _init() {
    if (this._initStarted) return;
    this._initStarted = true;
    await this._load();
  }

  private async _load() {
    if (!this.activeShop) {
      this._error = 'No active shop.';
      this._loading = false;
      return;
    }
    this._loading = true;
    this._error = null;
    const api = ApiFactory.authenticated();
    const [dR, sR] = await Promise.all([
      api.listDeliveries(this.activeShop.shopName, {
        status: this._statusFilter || undefined,
      }),
      api.listSuppliers(this.activeShop.shopName, true),
    ]);
    if (!dR.success || !dR.data) {
      this._error = dR.error ?? 'Failed to load deliveries.';
    } else {
      this._deliveries = dR.data;
    }
    if (sR.success && sR.data) {
      this._suppliersById = new Map(sR.data.map((s) => [s.id, s]));
    }
    this._loading = false;
  }

  private _openEdit(d: IDelivery) {
    router.navigate(
      resolveRouterPath(`deliveries/${encodeURIComponent(d.id)}`)
    );
  }

  private async _confirm(d: IDelivery, ev: Event) {
    ev.stopPropagation();
    if (!this.activeShop || this._busy) return;
    if (
      !confirm(
        'Confirm this delivery? This will update stock OnHand and the moving-average cost.'
      )
    )
      return;
    this._busy = true;
    const r = await ApiFactory.authenticated().confirmDelivery(
      this.activeShop.shopName,
      d.id
    );
    this._busy = false;
    if (!r.success || !r.data) {
      this._error = r.error ?? 'Confirm failed.';
      return;
    }
    const failures = r.data.lineFailures;
    if (failures.length > 0) {
      this._error = `Confirmed with ${failures.length} line failure${failures.length === 1 ? '' : 's'}: ${failures
        .map((f) => `${f.stockItemId || f.lineId} → ${f.reason}`)
        .join('; ')}`;
    }
    await this._load();
  }

  private async _cancel(d: IDelivery, ev: Event) {
    ev.stopPropagation();
    if (!this.activeShop || this._busy) return;
    if (!confirm('Cancel this delivery? No stock will change.')) return;
    this._busy = true;
    const r = await ApiFactory.authenticated().cancelDelivery(
      this.activeShop.shopName,
      d.id
    );
    this._busy = false;
    if (!r.success) {
      this._error = r.error ?? 'Cancel failed.';
      return;
    }
    await this._load();
  }

  private get _dataProvider() {
    return createArrayDataProvider<IDelivery>(() => this._deliveries, {
      filterFn: (item, text) => {
        const supplier = this._suppliersById.get(item.supplierId);
        return (
          (supplier?.name ?? '').toLowerCase().includes(text) ||
          item.supplierId.toLowerCase().includes(text) ||
          (item.deliveryNoteNumber ?? '').toLowerCase().includes(text) ||
          (item.purchaseOrderRefSnapshot ?? '').toLowerCase().includes(text)
        );
      },
    });
  }

  private _columns(isOwner: boolean): DataTableColumnDef[] {
    return [
      {
        title: 'Status',
        fieldName: 'status',
        sortable: true,
        width: '110px',
        displayValue: (_t, d: IDelivery) =>
          html`<sl-badge variant=${STATUS_VARIANT[d.status]} pill
            >${STATUS_LABEL[d.status]}</sl-badge
          >`,
      },
      {
        title: 'Supplier',
        fieldName: 'supplierId',
        sortable: true,
        minWidth: '160px',
        displayValue: (_t, d: IDelivery) => {
          const s = this._suppliersById.get(d.supplierId);
          return s?.name ?? d.supplierId;
        },
      },
      {
        title: 'DN #',
        fieldName: 'deliveryNoteNumber',
        sortable: true,
        width: '110px',
        displayValue: (_t, d: IDelivery) => d.deliveryNoteNumber || '—',
      },
      {
        title: 'PO',
        fieldName: 'purchaseOrderId',
        width: '120px',
        displayValue: (_t, d: IDelivery) =>
          d.purchaseOrderId
            ? html`<span style="font-size: 12px;"
                >${d.purchaseOrderRefSnapshot ?? 'linked'}</span
              >`
            : html`<span style="opacity: 0.55; font-size: 12px;">ad-hoc</span>`,
      },
      {
        title: 'Received',
        fieldName: 'receivedUtc',
        sortable: true,
        width: '150px',
        isDefaultSortDirectionAscending: false,
        displayValue: (_t, d: IDelivery) =>
          new Date(d.receivedUtc).toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          }),
      },
      {
        title: 'Lines',
        fieldName: 'lines',
        width: '70px',
        align: 'right',
        classes: 'money',
        displayValue: (_t, d: IDelivery) => String(d.lines.length),
      },
      {
        title: 'Subtotal',
        fieldName: 'subtotal',
        sortable: true,
        width: '110px',
        align: 'right',
        classes: 'money',
        displayValue: (_t, d: IDelivery) => d.subtotal.toFixed(2),
      },
      {
        title: '',
        fieldName: 'id',
        width: '108px',
        fixed: 'right',
        align: 'right',
        displayValue: (_t, d: IDelivery) => {
          if (!isOwner || d.status !== 'pending') return html``;
          return html`
            <div
              class="row-actions"
              @click=${(e: Event) => e.stopPropagation()}
            >
              <sl-icon-button
                name="check2-circle"
                label="Confirm"
                @click=${(e: Event) => this._confirm(d, e)}
              ></sl-icon-button>
              <sl-icon-button
                name="x-circle"
                label="Cancel"
                @click=${(e: Event) => this._cancel(d, e)}
              ></sl-icon-button>
            </div>
          `;
        },
      },
    ];
  }

  render() {
    const isOwner =
      this.activeShop &&
      this.userProfile?.hasRole(this.activeShop.shopName, 'owner');

    return html`
      <header class="bar">
        <h2 style="margin: 0;">Deliveries</h2>
        ${
          isOwner
            ? html`
                <sl-button
                  size="small"
                  variant="primary"
                  @click=${() => router.navigate(resolveRouterPath('deliveries/new'))}
                >
                  <sl-icon slot="prefix" name="plus"></sl-icon>
                  Receive new
                </sl-button>
              `
            : nothing
        }
      </header>

      <div class="filter-bar">
        <sl-select
          size="small"
          placeholder="All statuses"
          .value=${this._statusFilter}
          @sl-change=${(e: Event) => {
            this._statusFilter = (e.target as HTMLSelectElement).value;
            void this._load();
          }}
        >
          <sl-option value="">All</sl-option>
          <sl-option value="pending">Pending</sl-option>
          <sl-option value="confirmed">Confirmed</sl-option>
          <sl-option value="cancelled">Cancelled</sl-option>
        </sl-select>
      </div>

      ${
        this._error
          ? html`<sl-alert
              variant="danger"
              open
              closable
              @sl-after-hide=${() => (this._error = null)}
              >${this._error}</sl-alert
            >`
          : nothing
      }
      ${
        this._loading
          ? html`<p>Loading…</p>`
          : this._deliveries.length === 0
            ? html`<div class="empty">
                <p>No deliveries match the filter.</p>
              </div>`
            : html`
                <app-data-table
                  fill-height
                  clickrows
                  .columns=${this._columns(!!isOwner)}
                  .dataProvider=${this._dataProvider}
                  .keyevent=${(d: IDelivery) => d.id}
                  tablestyle="standardtable"
                  @rowclick=${(e: CustomEvent<{ item: IDelivery }>) =>
                    this._openEdit(e.detail.item)}
                ></app-data-table>
              `
      }
    `;
  }
}
